import {
  FindingAuditActionSchema,
  type FindingAuditAction,
} from '@terra/shared-contracts-findings';
import { getIntegrationDisplayName } from '@terra/shared-contracts-integrations';

export const formatProviderDisplayName = (provider: string): string =>
  getIntegrationDisplayName(provider);

export const formatRetestOutcomeDisplay = (outcome: string): string => {
  const normalized = outcome.replaceAll('_', ' ').trim();

  if (normalized.length === 0) {
    return outcome;
  }

  const lower = normalized.toLowerCase();

  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const PREVENTION_STATUS_LABELS: Record<string, string> = {
  generating: 'Generating',
  staged: 'Staged',
  active: 'Active',
  rejected: 'Rejected',
  removed: 'Removed',
  failed: 'Failed',
  expired: 'Expired',
  drifted: 'Drifted',
};

export const PREVENTION_AUDIT_ACTIONS = new Set<FindingAuditAction>([
  FindingAuditActionSchema.enum.PREVENTION_CONTROL_STAGED,
  FindingAuditActionSchema.enum.PREVENTION_CONTROL_ACTIVATED,
  FindingAuditActionSchema.enum.PREVENTION_CONTROL_REJECTED,
  FindingAuditActionSchema.enum.PREVENTION_CONTROL_REMOVED,
  FindingAuditActionSchema.enum.PREVENTION_CONTROL_DRIFTED,
]);

export const parsePreventionAuditValue = (
  raw: string | null | undefined,
): { status: string } | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (typeof record.status !== 'string') {
      return null;
    }
    return { status: record.status };
  } catch {
    return null;
  }
};

export const formatPreventionStatusLabel = (status: string): string =>
  PREVENTION_STATUS_LABELS[status] ?? status;

export const readMetadataString = (
  metadata: Record<string, unknown> | null,
  key: string,
): string | null => {
  if (!metadata || typeof metadata[key] !== 'string') {
    return null;
  }
  const value = metadata[key].trim();
  return value.length > 0 ? value : null;
};

export const formatAuditDateTime = ({
  value,
  dateFormatter,
}: {
  value: string;
  dateFormatter: { fromString: (date: string) => string };
}): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return dateFormatter.fromString(value);
};

export const readMetadataPrNumber = (
  metadata: Record<string, unknown> | null,
): string | null => {
  if (!metadata) {
    return null;
  }
  const prNumber = metadata.prNumber;
  if (typeof prNumber === 'number' || typeof prNumber === 'string') {
    const value = String(prNumber).trim();
    return value.length > 0 ? value : null;
  }
  return null;
};
